import type { DiscoveredRow } from '../../api/inventoree'
import { escapeHtml, fromNow, stamp } from '../format'
import type { TableColumn } from '../../ui/DataTable'
import { precheckBadge, readinessBadge } from './readiness'

/** Coloanele din DevicesModel (public/js/discovery.js), celula cu celula. */

export interface DiscoveredColumn extends TableColumn {
  name?: string
}

const MINUS = '<i class="fas fa-minus font-10"></i>'

export function agentHtml(r: DiscoveredRow): string {
  if (String(r.is_relay) === '1') {
    return '<i class="fas fa-broadcast-tower font-16 color-blue" aria-hidden="true" data-toggle="tooltip" data-placement="top" title="This machine is the scanning relay (managed)"></i>'
  }
  if (r.Agent) {
    return '<i class="fas fa-check-circle font-16 color-green2" aria-hidden="true" data-toggle="tooltip" data-placement="top" title="Agent installed"></i>'
  }
  /*
   * Aici se desena, pe `can_deploy`, ori "Eligible to deploy agent", ori un
   * semn rosu de interzis. Coloana aceea e 0 pe TOT tabelul - `canDeploy` nu e
   * trimis de niciun agent - deci fiecare masina fara agent purta semnul rosu.
   * Coloana asta raspunde acum la o singura intrebare - "e a noastra?" - iar
   * "se poate pune agent" e coloana de langa, socotita din porturi.
   */
  return '<i class="fas fa-minus font-10" aria-hidden="true" data-toggle="tooltip" data-placement="top" title="No agent on this machine"></i>'
}

/**
 * Pe care se poate pune agent, acum: doua pastile in aceeasi celula - deductia
 * din scanare si dovada de pe releu. A doua nu se deseneaza fara proba.
 */
export function deployReadyHtml(r: DiscoveredRow, now: Date = new Date()): string {
  return readinessBadge(r) + precheckBadge(r, now)
}

export function firstSawHtml(r: DiscoveredRow, now: Date = new Date()): string {
  const when = stamp(r.first_saw)
  if (!when) return MINUS
  return `<i class="far fa-calendar-check" aria-hidden="true"></i> ${fromNow(when, now)}`
}

export function lastSeenHtml(r: DiscoveredRow, now: Date = new Date()): string {
  const when = stamp(r.last_saw)
  let lastSaw = MINUS
  if (when) {
    const checking = String(r.checking ?? '')
    lastSaw = `<span class=" ${checking === '2' ? 'color-green2' : checking === '3' ? 'color-red' : ''} ">`
      + `<i class="fa ${checking === '2' ? 'fa-check-circle' : checking === '3' ? 'fa-exclamation-circle' : ''}" aria-hidden="true"></i> `
      + `${fromNow(when, now)}</span>`
  }
  if (String(r.checking) === '1') {
    lastSaw += ' <div style="margin-top:4px"><label class="label label-danger">Checking...</label></div>'
  }
  return lastSaw
}

export function macHtml(mac: string): string {
  return String(mac ?? '').replace(/-/g, ':')
}

export function deployStatusHtml(r: DiscoveredRow): string {
  switch (String(r.deploy_status)) {
    case '1':
      return '<label class="label label-warning">Pending</label>'
    case '2':
      return '<label class="label label-primary">Ok</label>'
    case '3':
      return `<label class="label label-danger" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.deploy_problem ?? '')}">Error</label>`
    default:
      return ''
  }
}

export function methodHtml(method: number | string | null): string {
  if (String(method) === '1') return '<span class="label label-primary">Network</span>'
  return '<span class="label label-danger">Manually</span>'
}

export function relayHtml(r: DiscoveredRow): string {
  if (r.relay_name) return escapeHtml(r.relay_name)
  if (r.ad_relay_id) return `<span class="text-muted">#${parseInt(String(r.ad_relay_id), 10)}</span>`
  return MINUS
}

export function typeHtml(r: DiscoveredRow): string {
  return `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type_name ?? '')}">${r.device_icon}</center>`
    + (r.od_name ? `<center><span class="color-light-grey" data-od-id="${r.od_id}">${escapeHtml(r.od_name)}</span></center>` : '')
}

export function nameHtml(r: DiscoveredRow): string {
  /* Numele scurt, apoi FQDN-ul (wks02 / wks02.cecdemo.local), mic si gri. */
  return `<p>${escapeHtml(r.device_name)}</p>`
    + (r.fqdn ? `<small class="color-light-grey" data-fqdn>${escapeHtml(r.fqdn)}</small>` : '')
    + (r.fqdn && r.friendly_name ? '<br>' : '')
    + (r.friendly_name ? `<span class="color-light-grey">${escapeHtml(r.friendly_name)}</span>` : '')
}

export function discoveredColumns(now: Date = new Date()): DiscoveredColumn[] {
  const cell = (fn: (r: DiscoveredRow) => string) => (row: Record<string, unknown>) => fn(row as unknown as DiscoveredRow)
  return [
    {
      index: 0, title: '', orderable: false, visible: true, width: 24, name: 'ID',
      html: () => '<center class="show_details"><i class="fas fa-plus-circle dd-toggle" style="cursor:pointer;color:#8a94a0"></i></center>',
    },
    {
      index: 1,
      title: '',
      titleHtml: '<center><input type="checkbox" class="allDevices"/></center>',
      orderable: false,
      visible: true,
      name: 'ID',
      html: cell((r) => `<center><input value="${r.ID}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>`),
    },
    { index: 2, title: 'Type', orderable: true, visible: true, name: 'type', html: cell((r) => typeHtml(r)) },
    { index: 3, title: 'Device name', orderable: true, visible: true, name: 'device_name', html: cell((r) => nameHtml(r)) },
    { index: 4, title: 'Agent', orderable: true, visible: true, name: 'Agent', html: cell((r) => agentHtml(r)) },
    /* Lista se deschide ordonata dupa coloana asta, dupa RANG, nu dupa cod. */
    { index: 5, title: 'Deploy ready', orderable: true, visible: true, name: 'deploy_rank', html: cell((r) => deployReadyHtml(r, now)) },
    { index: 6, title: 'First saw', orderable: true, visible: true, name: 'first_saw', html: cell((r) => firstSawHtml(r, now)) },
    { index: 7, title: 'Last seen', orderable: true, visible: true, name: 'last_saw', html: cell((r) => lastSeenHtml(r, now)) },
    { index: 8, title: 'Internal IP', orderable: true, visible: true, name: 'internal_ip', html: cell((r) => r.internal_ip) },
    { index: 9, title: 'Mac', orderable: true, visible: true, name: 'mac', html: cell((r) => macHtml(r.mac)) },
    { index: 10, title: 'OS', orderable: true, visible: true, name: 'os', html: cell((r) => r.os) },
    { index: 11, title: 'Manufacturer', orderable: true, visible: true, name: 'manufacturer', html: cell((r) => String(r.manufacturer_name ?? '')) },
    { index: 12, title: 'Deploy status', orderable: true, visible: true, name: 'deploy_status', html: cell((r) => deployStatusHtml(r)) },
    { index: 13, title: 'Method', orderable: true, visible: true, name: 'method', html: cell((r) => methodHtml(r.method)) },
    { index: 14, title: 'Discovered by', orderable: true, visible: true, name: 'relay_name', html: cell((r) => relayHtml(r)) },
    { index: 15, title: 'Actions', orderable: false, visible: true, name: 'ID', html: cell((r) => r.actions || MINUS) },
  ]
}
